import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Divider } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useTheme, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BuildIcon from "@mui/icons-material/Build";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import api from "../../config/axios";

const EXPENSE_TYPES = ["ads", "packaging", "material", "shipping", "total"];

const monthOptions = [
  { label: "All", value: "all" },
  { label: "This Month", value: "current" },
  { label: "Last Month", value: "last" },
  { label: "This Year", value: "year" },
];

function formatDisplayDate(value) {
  if (!value) return "-";
  try {
    // Normalize common MySQL datetime format to ISO for better parsing
    const normalized = typeof value === "string" ? value.replace(" ", "T") : value;
    const d = new Date(normalized);
    if (!isNaN(d.getTime())) {
      const day = d.getDate();
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      return `${day} ${month}, ${year}`;
    }
    // If backend returns already formatted date string
    return String(value);
  } catch (_) {
    return String(value);
  }
}

const defaultFormState = { type: "", description: "", amount: "" };

const getDefaultDescription = (type) => {
  const descriptions = {
    ads: "Advertisement expenses for marketing campaigns",
    packaging: "Packaging materials and supplies",
    material: "Raw materials and supplies for production",
    shipping: "Shipping and delivery costs"
  };
  return descriptions[type] || "";
};

const getTypeColor = (type, opacity = 1) => {
  const colors = {
    ads: `rgba(181, 29, 110, ${opacity})`,      // Ishqnama Pink
    packaging: `rgba(0, 86, 210, ${opacity})`,    // Royal Blue
    material: `rgba(30, 126, 52, ${opacity})`,   // Forest Green
    shipping: `rgba(183, 110, 0, ${opacity})`,    // Deep Amber
    total: `rgba(97, 97, 97, ${opacity})`,      // Slate
  };
  return colors[type] || `rgba(181, 29, 110, ${opacity})`;
};

const getTypeIcon = (type) => {
  const icons = {
    ads: CampaignIcon,
    packaging: Inventory2Icon,
    material: BuildIcon,
    shipping: LocalShippingIcon,
    total: AccountBalanceWalletIcon,
  };
  return icons[type] || AccountBalanceWalletIcon;
};

const Expenses = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [categorySummary, setCategorySummary] = useState({});

  const [typeFilter, setTypeFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchParams = useMemo(
    () => ({ type: typeFilter, month: monthFilter, page, limit }),
    [typeFilter, monthFilter, page, limit]
  );

  const fetchExpenses = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: fetchParams.page,
        limit: fetchParams.limit,
      };

      // Filter by type if not 'all' and if it's not the 'total' category which is for summary only
      if (fetchParams.type !== "all" && fetchParams.type !== "total") {
        params.type = fetchParams.type;
      }

      // Filter by month if not 'all'
      if (fetchParams.month !== "all") {
        params.month = fetchParams.month;
      }

      const response = await api.get("/expense.php", { params });
      const payload = response.data || {};
      const data = Array.isArray(payload?.data) ? payload.data : [];
      setItems(data);
      
      const totalCount = Number(payload?.total ?? data.length);
      const totalPages = Number(payload?.totalPages ?? 1);
      const currentPage = Number(payload?.page ?? fetchParams.page);
      
      setTotal(totalCount);
      setPages(totalPages);
      console.log({ currentPage, totalPages })
      setHasMore(currentPage < totalPages);
      setCategorySummary(payload?.category || {});
    } catch (err) {
      console.error("Expense fetch error:", err);
      console.error("Error response:", err.response);
      setError(
        err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to load expenses"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, monthFilter, page, limit]);

  const resetAndClose = () => {
    setOpenModal(false);
    setForm(defaultFormState);
    setEditingItem(null);
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setForm(defaultFormState);
    setOpenModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      type: item?.type || "",
      description: item?.description || "",
      amount: item?.amount?.toString?.() || String(item?.amount ?? ""),
    });
    setOpenModal(true);
  };

  // Delete is not supported by backend. Intentionally omitted.

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.amount || !form.description) {
      return;
    }
    const payload = {
      type: form.type,
      description: form.description,
      amount: Number(form.amount),
    };
    setSaving(true);
    try {
      if (editingItem) {
        const id = editingItem?.id ?? editingItem?.expense_id;
        await api.put("/create-expense.php", { id, ...payload });
      } else {
        await api.post("/create-expense.php", payload);
      }
      resetAndClose();
      // Refresh list, go back to first page to show new item
      setPage(1);
      fetchExpenses();
    } catch (err) {
      alert(
        err?.response?.data?.error || err?.response?.data?.message || "Failed to save expense"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangeForm = (field) => (e) => {
    const value = e.target.value;

    if (field === "type") {
      // Auto-fill description when type changes
      setForm((prev) => ({
        ...prev,
        [field]: value,
        description: getDefaultDescription(value)
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 }, background: '#FAF9F6' }}>
      <Box sx={{ mb: 2 }} />
      {/* Summary by category only */}
      {Object.keys(categorySummary).length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
            },
            gap: { xs: 2, md: 3 },
            mb: { xs: 5, md: 8 },
          }}
        >
          {EXPENSE_TYPES.map((type) => {
            const totalAmount = type === 'total'
              ? Object.values(categorySummary).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0)
              : categorySummary[type] || 0;
            const TypeIcon = getTypeIcon(type);
            const color = getTypeColor(type);

            return (
              <Card
                key={type}
                elevation={0}
                sx={{
                  borderRadius: "24px",
                  background: "#FFFFFF",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.02)",
                  transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                  "&:hover": {
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.06)",
                    transform: "translateY(-6px)",
                    borderColor: getTypeColor(type, 0.15),
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "16px",
                      background: getTypeColor(type, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: color,
                      mb: 3,
                    }}
                  >
                    <TypeIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      color: "text.primary",
                      mb: 1,
                      fontSize: { xs: "1.4rem", md: "1.8rem" },
                      lineHeight: 1,
                      letterSpacing: '-0.03em'
                    }}
                  >
                    ₹{totalAmount.toLocaleString('en-IN')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 800,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: '0.1em'
                    }}
                  >
                    {type}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: "24px",
          border: '1px solid rgba(0, 0, 0, 0.05)',
          background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(20px)",
          boxShadow: '0 10px 30px rgba(0,0,0,0.02)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          gap={3}
        >
          <Box sx={{ display: 'flex', gap: 2, flex: { xs: 1, sm: 'none' }, width: { xs: '100%', sm: 'auto' } }}>
            <FormControl size="small" sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                borderRadius: '16px',
                background: '#FFF',
                "& fieldset": { borderColor: 'rgba(0,0,0,0.08)' },
              }
            }}>
              <InputLabel id="type-filter-label" sx={{ fontWeight: 600 }}>Filter Category</InputLabel>
              <Select
                labelId="type-filter-label"
                label="Filter Category"
                value={typeFilter}
                onChange={(e) => { setPage(1); setTypeFilter(e.target.value); }}
              >
                <MenuItem value="all" sx={{ fontWeight: 600 }}>All Categories</MenuItem>
                {EXPENSE_TYPES.map((t) => (
                  <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                borderRadius: '16px',
                background: '#FFF',
                "& fieldset": { borderColor: 'rgba(0,0,0,0.08)' },
              }
            }}>
              <InputLabel id="month-filter-label" sx={{ fontWeight: 600 }}>Chronology</InputLabel>
              <Select
                labelId="month-filter-label"
                label="Chronology"
                value={monthFilter}
                onChange={(e) => { setPage(1); setMonthFilter(e.target.value); }}
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value} sx={{ fontWeight: 600 }}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
              color: "#FFFFFF !important",
              fontWeight: 900,
              textTransform: "none",
              borderRadius: '16px',
              px: 5,
              py: 2,
              fontSize: "0.95rem",
              boxShadow: '0 8px 25px rgba(181, 29, 110, 0.25)',
              width: { xs: '100%', sm: 'auto' },
              "&:hover": {
                background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                boxShadow: '0 12px 35px rgba(181, 29, 110, 0.35)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Add New Entry
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Desktop/tablet table view */}
        {!isMobile && (
          <>
            <TableContainer>
              <Table size="medium" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2.5, px: 4 }}>Classification</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2.5 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2.5 }}>Narrative</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2.5 }}>Chronology</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2.5, px: 4 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Loading expenses...</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && error && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>{error}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && !error && items?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>No expenses found matching your criteria.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && !error && items?.map((row, idx) => {
                    const id = row?.id ?? row?.expense_id ?? `${row.type}-${row.amount}-${row.date}`;
                    return (
                      <TableRow
                        key={id}
                        sx={{
                          "&:hover": { bgcolor: "rgba(0,0,0,0.015)" },
                          transition: 'background 0.2s'
                        }}
                      >
                        <TableCell sx={{ py: 3, px: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: getTypeColor(row.type)
                            }} />
                            <Typography sx={{ textTransform: "capitalize", fontWeight: 800, fontSize: "0.9rem" }}>
                              {row.type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Typography sx={{ fontWeight: 900, color: 'text.primary', fontSize: "0.95rem" }}>
                            ₹ {Number(row.amount ?? 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3, color: 'text.secondary', fontWeight: 600, maxWidth: 300, fontSize: '0.85rem' }}>
                          {row.description ? row.description : "-"}
                        </TableCell>
                        <TableCell sx={{ py: 3, color: 'text.secondary', fontWeight: 700, fontSize: '0.8rem' }}>
                          {formatDisplayDate(row.date || row.created_at)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 3, px: 4 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(row)}
                            sx={{
                              color: 'primary.main',
                              background: 'rgba(181, 29, 110, 0.05)',
                              '&:hover': { background: 'rgba(181, 29, 110, 0.1)', transform: 'translateY(-2px)' },
                              transition: 'all 0.2s',
                              borderRadius: '10px'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: 'wrap',
                gap: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Page {page} of {pages} • Total {total} expenses
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="pagination-limit-label">Per page</InputLabel>
                  <Select
                    labelId="pagination-limit-label"
                    label="Per page"
                    value={limit}
                    onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
                  >
                    {[10, 20, 50].map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ borderRadius: 2, borderColor: '#8B3555', color: '#8B3555', '&:hover': { borderColor: '#6B2840', bgcolor: 'rgba(139, 53, 85, 0.06)' } }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ borderRadius: 2, borderColor: '#8B3555', color: '#8B3555', '&:hover': { borderColor: '#6B2840', bgcolor: 'rgba(139, 53, 85, 0.06)' } }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          </>
        )}

        {/* Mobile card view */}
        {isMobile && (
          <Box sx={{ p: 2 }}>
            {loading && (
              <Typography variant="body2" align="center" sx={{ py: 4, fontWeight: 700, color: 'text.secondary' }}>Harmonizing Records...</Typography>
            )}
            <Stack spacing={2.5}>
              {items?.map((row) => {
                const id = row?.id ?? row?.expense_id ?? `${row.type}-${row.amount}-${row.date}`;
                return (
                  <Paper
                    key={id}
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      border: '1px solid rgba(0,0,0,0.06)',
                      background: "#FFFFFF",
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      "&:active": { transform: 'scale(0.98)' }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 6,
                      height: '100%',
                      bgcolor: getTypeColor(row.type),
                    }} />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.03)', color: 'text.secondary' }}>
                        <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: 900, fontSize: "0.65rem", letterSpacing: '0.05em' }}>
                          {row.type}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                        sx={{
                          color: 'primary.main',
                          background: 'rgba(181, 29, 110, 0.05)',
                          borderRadius: '8px'
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Stack>

                    <Typography sx={{ fontWeight: 900, color: 'text.primary', fontSize: "1.4rem", mb: 1.5, letterSpacing: '-0.02em' }}>
                      ₹ {Number(row.amount ?? 0).toLocaleString()}
                    </Typography>

                    {row.description && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 2, lineHeight: 1.5, fontSize: '0.85rem' }}>
                        {row.description}
                      </Typography>
                    )}

                    <Divider sx={{ mb: 2, opacity: 0.05 }} />

                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                        {formatDisplayDate(row.date || row.created_at)}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            <Box
              sx={{
                mt: 4,
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                borderRadius: '20px',
                bgcolor: 'rgba(0,0,0,0.02)'
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textAlign: 'center', fontSize: '0.75rem' }}>
                Journal Page {page} / {pages}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent="center">
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Paper>

      <Dialog
        open={openModal}
        onClose={saving ? undefined : resetAndClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '28px',
            p: 2,
            boxShadow: '0 25px 70px rgba(0,0,0,0.1)'
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 900,
            fontSize: "2rem",
            color: "text.primary",
            pb: 1,
          }}
        >
          {editingItem ? "Refine Entry" : "Log Investment"}
        </DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          <Box
            component="form"
            id="expense-form"
            onSubmit={handleSubmit}
            sx={{ mt: 1 }}
          >
            <Stack spacing={4}>
              <FormControl fullWidth required>
                <InputLabel id="type-label" sx={{ fontWeight: 600 }}>Expense Classification</InputLabel>
                <Select
                  labelId="type-label"
                  label="Expense Classification"
                  value={form.type}
                  onChange={handleChangeForm("type")}
                  sx={{
                    borderRadius: '16px',
                    background: 'rgba(0,0,0,0.02)',
                    "& fieldset": { border: 'none' },
                    "&.Mui-focused": { background: '#FFF', boxShadow: '0 0 0 1px var(--primary-main)' }
                  }}
                >
                  {EXPENSE_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize', fontWeight: 700 }}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                required
                type="number"
                label="Amount (₹)"
                value={form.amount}
                onChange={handleChangeForm("amount")}
                inputProps={{ min: 0, step: "0.01" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '16px',
                    background: 'rgba(0,0,0,0.02)',
                    "& fieldset": { border: 'none' },
                    "&.Mui-focused": { background: '#FFF', boxShadow: '0 0 0 1px var(--primary-main)' }
                  }
                }}
                InputLabelProps={{ sx: { fontWeight: 600 } }}
              />

              <TextField
                fullWidth
                label="Narrative Description"
                required
                value={form.description}
                onChange={handleChangeForm("description")}
                multiline
                minRows={3}
                placeholder="Detail the purpose of this financial outflow..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '16px',
                    background: 'rgba(0,0,0,0.02)',
                    "& fieldset": { border: 'none' },
                    "&.Mui-focused": { background: '#FFF', boxShadow: '0 0 0 1px var(--primary-main)' }
                  }
                }}
                InputLabelProps={{ sx: { fontWeight: 600 } }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 4, gap: 2 }}>
          <Button onClick={resetAndClose} disabled={saving} sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'none' }}>
            Discard
          </Button>
          <Button
            type="submit"
            form="expense-form"
            variant="contained"
            disabled={saving || !form.type || !form.amount}
            sx={{
              background: '#B51D6E',
              color: "#FFFFFF !important",
              fontWeight: 900,
              textTransform: "none",
              borderRadius: '16px',
              px: 5,
              py: 1.5,
              boxShadow: '0 8px 20px rgba(181, 29, 110, 0.25)',
              "&:hover": {
                background: '#D81B60',
                boxShadow: '0 12px 25px rgba(181, 29, 110, 0.35)'
              }
            }}
          >
            {saving ? "Logging..." : editingItem ? "Apply Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Expenses;


